/**
 * useGuardLocations Hook
 *
 * React Query hook for fetching enhanced guard locations with patrol progress.
 * Replaces the basic guard locations API with the enhanced dispatch version.
 *
 * Features:
 * - 2-minute fallback polling (socket events provide real-time updates)
 * - Pauses polling when tab is hidden, refetches on focus
 * - Includes patrol status and checkpoint progress
 * - Transform to frontend GuardLocation format with status
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchGuardLocations } from '../../../../../api/dispatch';
import { GuardLocation, GuardLocationRaw } from '../../components/guardLocation/types';
import { transformGuardData } from '../../components/guardLocation/utils';

/** Query key for guard locations */
export const GUARD_LOCATIONS_QUERY_KEY = ['guardLocations'];

/** Polling interval (2 minutes — socket events handle real-time, polling is fallback) */
const REFETCH_INTERVAL = 2 * 60 * 1000;

/** Stale time (60 seconds) */
const STALE_TIME = 60 * 1000;

export interface UseGuardLocationsOptions {
  /** Filter by customer ID */
  customerId?: number;
  /** Only return guards with recent locations (default: false for dashboard) */
  activeOnly?: boolean;
  /** Minutes threshold for "stale" status (default: 120) */
  staleMinutes?: number;
  /** Override polling interval (ms) */
  refetchInterval?: number;
  /** Disable auto-polling */
  enablePolling?: boolean;
}

export interface UseGuardLocationsResult {
  /** Guards with derived status fields */
  guards: GuardLocation[];
  /** Raw guards from API */
  rawGuards: GuardLocationRaw[];
  /** Total guard count */
  count: number;
  /** API response timestamp */
  timestamp: string | null;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether any fetch is in progress */
  isFetching: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if present */
  error: Error | null;
  /** Last successful update timestamp */
  dataUpdatedAt: number | undefined;
  /** Manually trigger a refresh */
  refetch: () => void;
}

/**
 * Hook to fetch enhanced guard locations with patrol progress
 *
 * @param options - Configuration options
 * @returns Guard locations with loading/error states
 */
export function useGuardLocations(
  options: UseGuardLocationsOptions = {}
): UseGuardLocationsResult {
  const {
    customerId,
    activeOnly = false, // Default to false to see all guards on dashboard
    staleMinutes = 120,
    refetchInterval = REFETCH_INTERVAL,
    enablePolling = true,
  } = options;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: [...GUARD_LOCATIONS_QUERY_KEY, { customerId, activeOnly, staleMinutes }],
    queryFn: () => fetchGuardLocations({ customerId, activeOnly, staleMinutes }),
    refetchInterval: enablePolling ? refetchInterval : false,
    staleTime: STALE_TIME,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  // Transform raw guards to include status and formatted fields
  const guards = useMemo(() => {
    if (!data?.guards) return [];
    return transformGuardData(data.guards);
  }, [data?.guards]);

  return {
    guards,
    rawGuards: data?.guards ?? [],
    count: data?.count ?? 0,
    timestamp: data?.timestamp ?? null,
    isLoading,
    isFetching,
    isError,
    error: error instanceof Error ? error : null,
    dataUpdatedAt,
    refetch,
  };
}

export default useGuardLocations;
